import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbExternalLink, TbMail, TbPaperclip } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { formatIsoTimestamp } from "../util/dateFormat";
import { formatBytes } from "../util/formatBytes";
import { type EmailThreadPayload, parseEmailThreadPayload } from "./emailSchema";

interface EmailThreadRendererProps {
  block: StreamingBlock;
}

// Web links arrive from tool output and are untrusted; only https URLs are safe
// to put in an href.
const safeGmailUrl = (url: string | undefined): string | undefined => (url?.startsWith("https://") ? url : undefined);

// Message headers and bodies are untrusted and rendered as text only.
export const EmailThreadRenderer = (props: EmailThreadRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<EmailThreadPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseEmailThreadPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("email_thread_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().messages.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbMail class="size-4 shrink-0 text-brand" />
                <span class="truncate text-sm font-medium text-primary-text">
                  {data().subject || rt.t("email_thread_card.title")}
                </span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().messages.length === 1
                    ? rt.t("email_thread_card.results_one")
                    : rt.t("email_thread_card.results", { count: String(data().messages.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().messages}>
                  {(message) => (
                    <li class="px-3 py-2">
                      <div class="flex items-baseline justify-between gap-3">
                        <span class="truncate text-sm font-medium text-primary-text">{message.from}</span>
                        <span class="shrink-0 text-xs text-muted-text">{formatIsoTimestamp(message.date)}</span>
                      </div>
                      <Show when={message.to.length > 0}>
                        <p class="mt-0.5 truncate text-xs text-muted-text">
                          {rt.t("email_thread_card.to_label")} {message.to.join(", ")}
                        </p>
                      </Show>
                      <p class="mt-1 line-clamp-2 text-xs text-muted-text">{message.snippet}</p>
                      <Show when={safeGmailUrl(message.webLink)}>
                        {(href) => (
                          <a
                            href={href()}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="mt-1 inline-flex items-center gap-1 text-xs text-brand hover:underline"
                          >
                            {rt.t("email_thread_card.open_link")}
                            <TbExternalLink class="size-3.5 shrink-0" aria-hidden="true" />
                          </a>
                        )}
                      </Show>
                      <Show when={message.attachments.length > 0}>
                        <ul
                          class="mt-2 flex flex-wrap gap-1.5"
                          aria-label={rt.t("email_thread_card.attachments_label")}
                        >
                          <For each={message.attachments}>
                            {(attachment) => (
                              <li class="flex max-w-full items-center gap-1.5 rounded-md border border-primary-border px-2 py-1">
                                <TbPaperclip class="size-3.5 shrink-0 text-muted-text" aria-hidden="true" />
                                <span class="truncate text-xs text-primary-text">{attachment.filename}</span>
                                <span class="shrink-0 text-xs text-muted-text">{formatBytes(attachment.size)}</span>
                              </li>
                            )}
                          </For>
                        </ul>
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
